import { Component } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AgGridAngular, AgGridModule } from "ag-grid-angular";
import {
  ColDef,
  ColGroupDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  ModuleRegistry,
  ProcessCellForExportParams,
  RowModelType,
  SideBarDef,
  ValidationModule,
} from "ag-grid-community";
import {
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  LicenseManager,
  ServerSideRowModelModule,
} from "ag-grid-enterprise";
import { CommonModule } from "@angular/common";
import { LiabilityRow, QueryRequest, QueryResponse } from "../../models/ratings.type";
import { debounceTime, Observable, Subject, takeUntil } from "rxjs";
import { environment } from "../../../environments/environment";
import { RatingsService } from "../../shared/services/ratings/ratings.service";
import { transformFilterModelForApi } from "../../shared/services/ratings/ratings-api.helpers";
import { compareDates, dateFilterComparator, formatDMY } from "../../shared/utils/date-helpers";
import { UserService } from "../../shared/services/user.service";
import { Router } from "@angular/router";
import { BookmarksComponent } from "../bookmarks/bookmarks.component";
import { MatSidenavModule } from "@angular/material/sidenav";
import { AppraisalDataService } from "../../shared/services/appraisal-data.service";

@Component({
  selector: 'app-liability',
  standalone: true,
  imports: [CommonModule, AgGridModule, MatSidenavModule, BookmarksComponent],
  templateUrl: './liability.component.html',
  styleUrl: './liability.component.scss'
})
export class LiabilityComponent {
  rowModelType: RowModelType = "serverSide";
  bookmarkGrid: string = "liability";
  private api!: GridApi;
  private destroy$ = new Subject<void>();
  quickFilter$ = new Subject<string>();
  userData: any;
  columnDefs: ColDef[] = [
    { field: 'companyName', headerName: 'Company', sortable: true, hide: true, filter: 'agTextColumnFilter', minWidth: 200 },
    { field: 'clientReference', headerName: 'Client Ref', sortable: true, filter: 'agTextColumnFilter' },
    { field: 'addressPrimary', headerName: 'Address', sortable: true, filter: 'agTextColumnFilter', minWidth: 220 },
    { field: 'cityName', headerName: 'City', sortable: true, filter: 'agTextColumnFilter' },
    { field: 'countryName', headerName: 'Country', sortable: true, filter: 'agTextColumnFilter' },
    { field: 'hereditamentReference', headerName: 'Hereditament Ref', sortable: true, filter: 'agTextColumnFilter' },
    {
      field: 'liabilityStartDate',
      headerName: 'Liability Start',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    {
      field: 'liabilityEndDate',
      headerName: 'Liability End',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    { field: 'currentRateableValue', headerName: 'Rateable Value', sortable: true, filter: 'agNumberColumnFilter' },
    { field: 'liabilityYear2023', headerName: '23/24', sortable: true, filter: 'agNumberColumnFilter' },
    { field: 'liabilityYear2024', headerName: '24/25', sortable: true, filter: 'agNumberColumnFilter' },
    { field: 'liabilityYear2025', headerName: '25/26', sortable: true, filter: 'agNumberColumnFilter' },
    { field: 'liabilityYear2026', headerName: '26/27', sortable: true, filter: 'agNumberColumnFilter' },
    { field: 'revaluationYear', headerName: 'Revaluation Period', sortable: true, hide: true, filter: 'agTextColumnFilter' },
  ];

  gridOptions: GridOptions = {
    rowModelType: 'serverSide',
    cacheBlockSize: 200,
    maxBlocksInCache: 10,
    blockLoadDebounceMillis: 50,
    sideBar: {
      toolPanels: ['filters'],   // shows Filters in the sidebar
      // defaultToolPanel: 'filters'  <-- remove this if present
    },
    defaultColDef: {
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
    }
  };

  excelStyles = [
    { id: 'header', font: { bold: true } },
    { id: 'int', numberFormat: { format: '#,##0' } },
    { id: 'number', numberFormat: { format: '#,##0.00' } },
    { id: 'date', dataType: 'dateTime', numberFormat: { format: 'dd-mm-yyyy' } }
  ];

  constructor(private http: HttpClient, private apiSvc: RatingsService, private userService: UserService, public router: Router,
    private appraisalDataService: AppraisalDataService
  ) {
    LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-078719}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Cushman_&_Wakefield_Global,_Inc}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Charts_and_AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{18_June_2026}____[v3]_[0102]_MTc4MTczNzIwMDAwMA==3d83d461ffd91d4aa36e813251f26c82");
    this.quickFilter$.pipe(debounceTime(250), takeUntil(this.destroy$)).subscribe(q => {
      this.api?.setFilterModel(this.api?.getFilterModel() || {}); // ensure model event
    });
  }

  async ngOnInit(){
    this.userService.userData$.subscribe((data) => {
      this.userData = data;
      if (!this.userData.roles.includes('Ratings')) {
        this.router.navigate(['/404']);
      }
    });
  }

  onGridReady(e: GridReadyEvent) {
    this.api = e.api;
    this.api.setGridOption('serverSideDatasource', this.makeDatasource());
  }

  onExport() {
    if (!this.api) return;

    const today = new Date();
    const fileName = `liabilities_${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}.xlsx`;
    const columnKeys = this.api.getAllDisplayedColumns().map(c => c.getColId());

    const hasExcel = typeof (this.api as any).exportDataAsExcel === 'function';

    const processCell = (p: ProcessCellForExportParams) => {
      const field = p.column.getColDef().field;
      const dateFields = new Set(['liabilityStartDate', 'liabilityEndDate']);
      if (field && dateFields.has(field) && p.value) {
        const d = new Date(p.value);
        if (!isNaN(d.getTime())) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
      }
      return p.value;
    };

    if (hasExcel) {
      // Excel (Enterprise)
      (this.api as any).exportDataAsExcel({
        fileName,
        sheetName: 'Liabilities',
        columnKeys,
        processCellCallback: processCell,
        excelStyles: this.excelStyles,
        // NOTE: With Server-Side Row Model, this exports the rows currently loaded in the client.
        // For true all-rows export, wire server-side Excel export in your datasource on the backend.
      });
    } else {
      // CSV fallback (Community)
      this.api.exportDataAsCsv({
        fileName: fileName.replace('.xlsx', '.csv'),
        columnKeys,
        processCellCallback: processCell
      });
    }
  }

  makeDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const agReq = params.request;

        const body: QueryRequest = {
          startRow: agReq.startRow ?? 0,
          endRow: agReq.endRow ?? 0,
          sortModel: (agReq.sortModel || []).map(s => ({ colId: s.colId, sort: s.sort as any })),
          filterModel: transformFilterModelForApi(agReq.filterModel || {}),
          quickFilter: (document.getElementById('quickFilterBox') as HTMLInputElement)?.value || null,
          select: this.currentProjection()
        };

        this.apiSvc.liabilityQuery(body).subscribe({
          next: (resp) => {
            params.success({ rowData: resp.rows as LiabilityRow[], rowCount: resp.lastRow });
          },
          error: (err) => {
            console.error('Query failed', err);
            params.fail();
          }
        });
      }
    };
  }

  currentProjection(): string[] {
    const displayed = (this.api?.getAllDisplayedColumns() || [])
      .map(c => c.getColDef().field!);

    // also include columns referenced by filters / sorts
    const fm = this.api?.getFilterModel() || {};
    const sm = this.api?.getColumnState()
      ?.filter(s => s.sort)            // currently sorted columns
      .map(s => s.colId) || [];

    const needed = Array.from(new Set<string>([...displayed, ...Object.keys(fm), ...sm]));

    const map = (f: string) => {
      switch (f) {
        case 'id': return 'ID';
        case 'clientReference': return 'ClientReference';
        case 'addressPrimary': return 'AddressPrimary';
        case 'cityName': return 'CityName';
        case 'countryName': return 'CountryName';
        case 'hereditamentReference': return 'HereditamentReference';
        case 'liabilityStartDate': return 'LiabilityStartDate';
        case 'liabilityEndDate': return 'LiabilityEndDate';
        case 'currentRateableValue': return 'CurrentRateableValue';
        case 'revaluationID': return 'RevaluationID';
        case 'revaluationYear': return 'RevaluationYear';
        case 'companyName': return 'CompanyName';
        case 'liabilityYear2023': return 'LiabilityYear2023';
        case 'liabilityYear2024': return 'LiabilityYear2024';
        case 'liabilityYear2025': return 'LiabilityYear2025';
        case 'liabilityYear2026': return 'LiabilityYear2026';
        default: return f;
      }
    };

    return needed.map(map);
  }

  onQuickFilterChange(value: string) {
    this.quickFilter$.next(value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isBookmarksPanelOpen: boolean = false;
  ontoggleBookmarkSidenav() {
    let state = this.api.getState();
    this.appraisalDataService.setGridStateData(state);
    this.isBookmarksPanelOpen = !this.isBookmarksPanelOpen;
  }
  onStateUpdated(): void {
    let state = this.api.getState();
    this.appraisalDataService.setGridStateData(state);
  }

  loadBookmark() {
    let state = this.appraisalDataService.getGridStateData();
    this.api.setState(state);
  }
}
